import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, User, Calendar, Send } from 'lucide-react';
import { Button } from './ui/button';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';

export default function MessagesDropdown() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const dropdownRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedConversation(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, []);

  // Fetch conversations when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  // Scroll to bottom when messages change (container-only, never the page)
  useEffect(() => {
    let box = messagesEndRef.current?.parentElement;
    for (let i = 0; box && i < 3; i++) {
      const oy = getComputedStyle(box).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      box = box.parentElement;
    }
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  const fetchUnreadCount = async () => {
    try {
      const response = await apiClient.get('/messages/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      // Use the dedicated conversations endpoint
      const response = await apiClient.get('/messages/conversations');
      const conversationsData = response.data;
      
      // Enrich with artist data for display
      const enrichedConversations = await Promise.all(
        conversationsData.map(async (conv) => {
          try {
            const artistRes = await apiClient.get(`/artists/${conv.artist_id}`);
            return {
              booking: {
                booking_id: conv.booking_id,
                artist_id: conv.artist_id,
                event_date: conv.event_date,
                booking_status: conv.booking_status,
                payment_status: conv.payment_status
              },
              artist: artistRes.data,
              unreadCount: conv.unread_count,
              lastMessage: conv.last_message,
              otherPartyName: conv.other_party_name,
              isArtistView: conv.is_artist_view
            };
          } catch (err) {
            return {
              booking: {
                booking_id: conv.booking_id,
                artist_id: conv.artist_id,
                event_date: conv.event_date,
                booking_status: conv.booking_status,
                payment_status: conv.payment_status
              },
              artist: null,
              unreadCount: conv.unread_count,
              lastMessage: conv.last_message,
              otherPartyName: conv.other_party_name,
              isArtistView: conv.is_artist_view
            };
          }
        })
      );
      
      setConversations(enrichedConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Fallback to old method if new endpoint fails
      try {
        const bookingsRes = await apiClient.get('/bookings');
        const paidBookings = bookingsRes.data.filter(b => 
          b.payment_status === 'paid' || b.booking_status === 'confirmed'
        );
        
        const conversationsData = await Promise.all(
          paidBookings.map(async (booking) => {
            try {
              const [messagesRes, artistRes] = await Promise.all([
                apiClient.get(`/messages/booking/${booking.booking_id}`),
                apiClient.get(`/artists/${booking.artist_id}`)
              ]);
              return {
                booking,
                artist: artistRes.data,
                messages: messagesRes.data,
                unreadCount: messagesRes.data.filter(m => m.receiver_id === user?.user_id && !m.read).length,
                lastMessage: messagesRes.data[messagesRes.data.length - 1],
                otherPartyName: artistRes.data?.bio?.substring(0, 30) || 'Artist',
                isArtistView: false
              };
            } catch (err) {
              return null;
            }
          })
        );
        
        setConversations(conversationsData.filter(c => c !== null));
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (bookingId) => {
    try {
      const response = await apiClient.get(`/messages/booking/${bookingId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.booking.booking_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const receiverId = user?.user_id === selectedConversation.booking.user_id 
        ? selectedConversation.artist.user_id 
        : selectedConversation.booking.user_id;

      await apiClient.post('/messages', {
        booking_id: selectedConversation.booking.booking_id,
        receiver_id: receiverId,
        content: newMessage
      });

      setNewMessage('');
      fetchMessages(selectedConversation.booking.booking_id);
      fetchUnreadCount();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    setSelectedConversation(null);
    setMessages([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        data-testid="nav-messages"
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        className="hover:bg-white/10 relative"
      >
        <MessageCircle size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed sm:absolute left-1/2 sm:left-auto sm:right-0 -translate-x-1/2 sm:translate-x-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-80 max-h-[70vh] sm:max-h-[500px] bg-gray-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-violet-900/30">
            {selectedConversation ? (
              <>
                <button 
                  onClick={handleBack}
                  className="text-white/60 hover:text-white mr-2"
                >
                  ←
                </button>
                <div className="flex-1">
                  <h3 className="font-semibold text-white truncate">
                    {selectedConversation.artist?.bio?.substring(0, 20) || 'Artist'}
                  </h3>
                  <p className="text-xs text-white/60">
                    {new Date(selectedConversation.booking.event_date).toLocaleDateString()}
                  </p>
                </div>
              </>
            ) : (
              <h3 className="font-semibold text-white">Messages</h3>
            )}
            <button 
              onClick={() => { setIsOpen(false); setSelectedConversation(null); }}
              className="text-white/60 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: selectedConversation ? '350px' : '400px' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : selectedConversation ? (
              /* Messages View */
              <div className="p-3 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-white/40">
                    <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs">Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-xl px-3 py-2 ${
                          msg.sender_id === user?.user_id
                            ? 'bg-violet-600 text-white'
                            : 'bg-white/10 text-white'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-[10px] opacity-60 mt-1">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            ) : conversations.length === 0 ? (
              /* Empty State */
              <div className="text-center py-12 text-white/40">
                <MessageCircle size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">No conversations yet</p>
                <p className="text-xs mt-1">Complete a booking to start messaging</p>
              </div>
            ) : (
              /* Conversations List */
              <div className="divide-y divide-white/5">
                {conversations.map((conv) => (
                  <button
                    key={conv.booking.booking_id}
                    onClick={() => handleConversationClick(conv)}
                    className="w-full p-4 hover:bg-white/5 transition text-left flex items-start gap-3"
                  >
                    {conv.artist?.media_urls?.[0] && !conv.isArtistView ? (
                      <img 
                        src={conv.artist.media_urls[0]} 
                        alt="Artist"
                        className="w-10 h-10 rounded-full object-cover border-2 border-violet-500"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center border-2 border-violet-500">
                        <User size={18} className="text-violet-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white truncate">
                          {conv.otherPartyName || conv.artist?.genres?.[0] || 'Artist'}
                        </span>
                        {conv.unreadCount > 0 && (
                          <span className="w-5 h-5 bg-violet-500 rounded-full text-xs flex items-center justify-center text-white">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-white/40 mt-0.5">
                        <Calendar size={10} />
                        {new Date(conv.booking.event_date).toLocaleDateString()}
                        {conv.isArtistView && <span className="ml-1 text-violet-400">(You&apos;re the artist)</span>}
                      </div>
                      {conv.lastMessage && (
                        <p className="text-xs text-white/60 truncate mt-1">
                          {conv.lastMessage.sender_id === user?.user_id ? 'You: ' : ''}
                          {conv.lastMessage.content}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message Input (only when conversation is selected) */}
          {selectedConversation && (
            <form onSubmit={handleSendMessage} className="p-3 border-t border-white/10 bg-black/20">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-violet-500"
                  disabled={sending}
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={sending || !newMessage.trim()}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Send size={16} />
                </Button>
              </div>
            </form>
          )}

          {/* Footer - View All */}
          {!selectedConversation && conversations.length > 0 && (
            <div className="p-3 border-t border-white/10 bg-black/20">
              <Button
                variant="ghost"
                className="w-full text-violet-400 hover:text-violet-300 text-sm"
                onClick={() => {
                  setIsOpen(false);
                  navigate(user?.role === 'admin' ? '/admin' : '/dashboard?tab=messages');
                }}
              >
                View All Messages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
